import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API_BASE } from "../utils/api";

export default function ResetPassword() {
  const [params]       = useSearchParams();
  const navigate       = useNavigate();
  const token          = params.get("token") || "";
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState("");
  const [showPass,  setShowPass]  = useState(false);

  useEffect(() => { if (!token) navigate("/forgot-password"); }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/email-auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const d = await res.json();
      if (res.ok) setDone(true);
      else setError(d.detail || "Reset failed. Link may have expired.");
    } catch { setError("Connection error. Please try again."); }
    finally { setLoading(false); }
  };

  const strength = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 ? 2 : /[A-Z]/.test(password) && /[0-9]/.test(password) ? 4 : 3;
  const strengthLabel = ["","Weak","Fair","Good","Strong"];
  const strengthColor = ["","bg-red-500","bg-amber-500","bg-blue-500","bg-luxury-cta"];

  return (
    <div className="min-h-screen bg-luxury-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-10 h-10 glass-gold rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-luxury-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <span className="text-xl font-bold text-luxury-text">Tax<span className="text-luxury-gold">Vault</span></span>
        </div>

        <div className="glass-strong rounded-2xl p-8 border border-white/[0.07]">
          {done ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-5 glass rounded-2xl flex items-center justify-center text-3xl border border-luxury-cta/30">✅</div>
              <h2 className="text-2xl font-bold text-luxury-text mb-2">Password Reset!</h2>
              <p className="text-luxury-secondary text-sm mb-6">Your password has been updated successfully. You can now sign in with your new password.</p>
              <Link to="/login" className="block w-full text-center py-3 bg-luxury-gold hover:bg-luxury-gold-dim text-white font-bold rounded-xl transition-all text-sm">
                Sign In Now →
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-luxury-text mb-1">Set New Password</h2>
              <p className="text-luxury-secondary text-sm mb-6">Choose a strong password for your TaxVault account.</p>

              {error && (
                <div className="glass border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-5 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">New Password</label>
                  <div className="relative">
                    <input type={showPass ? "text" : "password"} required value={password}
                      onChange={e => { setPassword(e.target.value); setError(""); }}
                      placeholder="Minimum 8 characters"
                      className="w-full px-4 py-3 pr-11 rounded-xl border border-luxury-border bg-luxury-elevated text-luxury-text placeholder-luxury-muted focus:outline-none focus:border-luxury-gold/50 focus:ring-2 focus:ring-luxury-gold/10 text-sm transition-all" />
                    <button type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-luxury-muted hover:text-luxury-secondary transition-colors p-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        {showPass
                          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                          : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></>
                        }
                      </svg>
                    </button>
                  </div>
                  {password && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {[1,2,3,4].map(i => (
                          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= strength ? strengthColor[strength] : "bg-luxury-border"}`} />
                        ))}
                      </div>
                      <span className="text-xs text-luxury-muted">{strengthLabel[strength]}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Confirm Password</label>
                  <input type="password" required value={confirm}
                    onChange={e => { setConfirm(e.target.value); setError(""); }}
                    placeholder="Re-enter your password"
                    className={`w-full px-4 py-3 rounded-xl border bg-luxury-elevated text-luxury-text placeholder-luxury-muted focus:outline-none text-sm transition-all
                      ${confirm && password !== confirm ? "border-red-500/50 focus:ring-2 focus:ring-red-500/10" : "border-luxury-border focus:border-luxury-gold/50 focus:ring-2 focus:ring-luxury-gold/10"}`} />
                  {confirm && password !== confirm && (
                    <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>

                <button type="submit" disabled={loading || !password || !confirm || password !== confirm || password.length < 8}
                  className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all active:scale-95 mt-2
                    ${loading || !password || !confirm || password !== confirm || password.length < 8
                      ? "opacity-50 cursor-not-allowed glass text-luxury-muted"
                      : "bg-luxury-gold hover:bg-luxury-gold-dim text-white shadow-md"}`}>
                  {loading ? "Resetting..." : "Reset Password →"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
